import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { getSplits, listPlans } from "./services/trainingPlansV2Service";

/**
 * Pick a workout from another plan belonging to the same client, then hand the
 * chosen split back to the editor to append (with exercises).
 */
export default function CopyWorkoutFromPlanDialog({
  open,
  onClose,
  onConfirm,
  currentPlanId,
  clientUID,
}) {
  const [planSearch, setPlanSearch] = React.useState("");
  const [plans, setPlans] = React.useState([]);
  const [plansLoading, setPlansLoading] = React.useState(false);
  const [plansError, setPlansError] = React.useState("");
  const [plansHasMore, setPlansHasMore] = React.useState(false);
  const [plansLastDoc, setPlansLastDoc] = React.useState(null);

  const [selectedPlanId, setSelectedPlanId] = React.useState("");
  const [selectedPlanName, setSelectedPlanName] = React.useState("");
  const [splits, setSplits] = React.useState([]);
  const [splitsLoading, setSplitsLoading] = React.useState(false);
  const [splitsError, setSplitsError] = React.useState("");
  const [selectedSplitId, setSelectedSplitId] = React.useState("");

  const [confirming, setConfirming] = React.useState(false);
  const [confirmError, setConfirmError] = React.useState("");

  const selectedSplit = React.useMemo(
    () => splits.find((s) => s.id === selectedSplitId) || null,
    [splits, selectedSplitId],
  );

  const resetSelection = React.useCallback(() => {
    setSelectedPlanId("");
    setSelectedPlanName("");
    setSplits([]);
    setSplitsError("");
    setSelectedSplitId("");
    setConfirmError("");
  }, []);

  const loadPlans = React.useCallback(
    async ({ search = "", append = false, startAfterDoc = null } = {}) => {
      if (!clientUID) {
        setPlans([]);
        setPlansHasMore(false);
        setPlansLastDoc(null);
        setPlansError("This action is only available on a client plan.");
        return;
      }
      setPlansLoading(true);
      setPlansError("");
      try {
        const result = await listPlans({
          type: "client",
          clientUID,
          search,
          status: "all",
          sort: "updated_desc",
          pageSize: 25,
          startAfterDoc,
        });
        const next = (result?.plans || []).filter((p) => p?.id && p.id !== currentPlanId);
        setPlans((prev) => (append ? [...prev, ...next] : next));
        setPlansHasMore(Boolean(result?.hasMore));
        setPlansLastDoc(result?.lastDoc || null);
      } catch (error) {
        setPlansError(error?.message || "Unable to load this client's plans.");
        if (!append) {
          setPlans([]);
          setPlansHasMore(false);
          setPlansLastDoc(null);
        }
      } finally {
        setPlansLoading(false);
      }
    },
    [clientUID, currentPlanId],
  );

  React.useEffect(() => {
    if (!open) return;
    setPlanSearch("");
    resetSelection();
    setPlans([]);
    setPlansLastDoc(null);
    setPlansHasMore(false);
    setPlansError("");
  }, [open, clientUID, currentPlanId, resetSelection]);

  React.useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const delayMs = planSearch ? 300 : 0;
    const timer = window.setTimeout(() => {
      if (cancelled) return;
      // Search changes clear the workout pick; initial open already reset above.
      if (planSearch) {
        resetSelection();
      }
      void loadPlans({ search: planSearch.trim() });
    }, delayMs);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [open, planSearch, loadPlans, resetSelection]);

  const handleSelectPlan = React.useCallback(async (plan) => {
    if (!plan?.id) return;
    setSelectedPlanId(plan.id);
    setSelectedPlanName(plan.name || "Untitled plan");
    setSelectedSplitId("");
    setSplits([]);
    setSplitsError("");
    setConfirmError("");
    setSplitsLoading(true);
    try {
      const nextSplits = await getSplits(plan.id);
      setSplits(Array.isArray(nextSplits) ? nextSplits : []);
    } catch (error) {
      setSplitsError(error?.message || "Unable to load workouts for that plan.");
      setSplits([]);
    } finally {
      setSplitsLoading(false);
    }
  }, []);

  const handleConfirm = React.useCallback(async () => {
    if (!selectedSplit) {
      setConfirmError("Select a workout to copy.");
      return;
    }
    setConfirming(true);
    setConfirmError("");
    try {
      await onConfirm({
        sourcePlanId: selectedPlanId,
        sourcePlanName: selectedPlanName,
        sourceSplit: selectedSplit,
      });
      onClose?.();
    } catch (error) {
      setConfirmError(error?.message || "Unable to copy that workout.");
    } finally {
      setConfirming(false);
    }
  }, [onClose, onConfirm, selectedPlanId, selectedPlanName, selectedSplit]);

  const canConfirm = Boolean(selectedSplit) && !confirming && !splitsLoading;

  return (
    <Dialog open={open} onClose={confirming ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Copy workout from another plan</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Choose another plan for this client, then pick a workout. It will be added at the bottom
            of this plan (with exercises). You can reorder it after.
          </Typography>

          {!clientUID ? (
            <Alert severity="info">Only available when editing a client plan.</Alert>
          ) : (
            <>
              <TextField
                size="small"
                label="Search plans"
                value={planSearch}
                onChange={(e) => setPlanSearch(e.target.value)}
                fullWidth
              />

              {plansError ? <Alert severity="error">{plansError}</Alert> : null}

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Client plans
                </Typography>
                {plansLoading && plans.length === 0 ? (
                  <Stack alignItems="center" sx={{ py: 2 }}>
                    <CircularProgress size={24} />
                  </Stack>
                ) : plans.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No other plans found for this client.
                  </Typography>
                ) : (
                  <List dense disablePadding sx={{ maxHeight: 200, overflow: "auto", border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                    {plans.map((plan) => (
                      <ListItemButton
                        key={plan.id}
                        selected={selectedPlanId === plan.id}
                        onClick={() => void handleSelectPlan(plan)}
                        disabled={confirming}
                      >
                        <ListItemText
                          primary={plan.name || "Untitled plan"}
                          secondary={plan.status ? `Status: ${plan.status}` : null}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                )}
                {plansHasMore ? (
                  <Button
                    size="small"
                    sx={{ mt: 1 }}
                    disabled={plansLoading || confirming}
                    onClick={() =>
                      void loadPlans({
                        search: planSearch.trim(),
                        append: true,
                        startAfterDoc: plansLastDoc,
                      })
                    }
                  >
                    {plansLoading ? "Loading…" : "Load more"}
                  </Button>
                ) : null}
              </Box>

              {selectedPlanId ? (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Workouts in {selectedPlanName}
                  </Typography>
                  {splitsError ? <Alert severity="error" sx={{ mb: 1 }}>{splitsError}</Alert> : null}
                  {splitsLoading ? (
                    <Stack alignItems="center" sx={{ py: 2 }}>
                      <CircularProgress size={24} />
                    </Stack>
                  ) : splits.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      That plan has no workouts.
                    </Typography>
                  ) : (
                    <List dense disablePadding sx={{ maxHeight: 220, overflow: "auto", border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                      {splits.map((split, index) => (
                        <ListItemButton
                          key={split.id}
                          selected={selectedSplitId === split.id}
                          onClick={() => setSelectedSplitId(split.id)}
                          disabled={confirming}
                        >
                          <ListItemText
                            primary={split.name || `Workout ${index + 1}`}
                            secondary={
                              split.isRestSplit
                                ? `Rest Day • Position ${index + 1}`
                                : `Workout • Position ${index + 1}`
                            }
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  )}
                </Box>
              ) : null}

              {confirmError ? <Alert severity="error">{confirmError}</Alert> : null}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={confirming}>
          Cancel
        </Button>
        <Button variant="contained" color="primary" onClick={() => void handleConfirm()} disabled={!canConfirm}>
          {confirming ? "Copying…" : "Copy workout"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
